import { getName, OntologyType } from "core/model/utils/ontologies";
import { useGetOntologies } from "core/model/utils/ontologies/hooks";
import CheckboxGroup, {
  CheckboxGroupBaseProps,
} from "ds_legacy/components/CheckboxGroup";
import { getOrderedItems } from "ds_legacy/components/ChipGroup";
import { CSSProperties, ReactNode } from "react";
import { useTranslations } from "translations";

export type OntologySection = {
  heading: ReactNode;
  ids: number[];
  key: string;
};

export type OntologyCheckboxGroupProps = CheckboxGroupBaseProps & {
  captions?: Record<string, any>;
  isRequired?: boolean;
  labelStyle?: CSSProperties;
  order?: Record<string, any>;
  sections?: OntologySection[];
  shouldSort?: boolean;
  type: OntologyType;
};

type OntologyCheckboxItem = {
  id: number;
  label: string;
  RadioCaption?: string | null;
  sectionHeading?: ReactNode;
  sectionKey?: string;
};

function toOntologyCheckboxItem(
  item: { label: string; value: number },
  captions?: Record<string, any>,
): OntologyCheckboxItem {
  return {
    id: item.value,
    label: item.label,
    RadioCaption:
      captions &&
      Object.keys(captions).filter((key: any) => key == item.value).length === 1
        ? captions[item.value]
        : undefined,
  };
}

function orderItemsBySections(
  baseItems: OntologyCheckboxItem[],
  sections: OntologySection[],
): OntologyCheckboxItem[] {
  const placed = new Set<number>();
  const result: OntologyCheckboxItem[] = [];

  for (const section of sections) {
    const sectionIds = new Set(section.ids);
    for (const item of baseItems) {
      if (sectionIds.has(item.id)) {
        result.push({
          ...item,
          sectionHeading: section.heading,
          sectionKey: section.key,
        });
        placed.add(item.id);
      }
    }
  }

  for (const item of baseItems) {
    if (!placed.has(item.id)) {
      result.push(item);
    }
  }

  return result;
}

export function useOntologyCheckboxGroup({
  captions,
  order,
  sections,
  shouldSort,
  type,
}: Pick<
  OntologyCheckboxGroupProps,
  "captions" | "shouldSort" | "type" | "order" | "sections"
>) {
  const getOntologies = useGetOntologies();
  const values = getOntologies({ sort: shouldSort, type });
  const sortedValues = getOrderedItems(order, values);

  const baseItems = sortedValues.map((item) =>
    toOntologyCheckboxItem(item, captions),
  );

  if (!sections?.length) return baseItems;

  return orderItemsBySections(baseItems, sections);
}

export default function OntologyCheckboxGroup({
  captions,
  elementName,
  exclude,
  formControlStyle,
  include,
  isRequired,
  label,
  labelStyle,
  labelWrapperStyle,
  order,
  sections,
  shouldSort,
  sideMutation,
  type,
  wrapperStyle,
}: OntologyCheckboxGroupProps) {
  const translations = useTranslations();
  const items = useOntologyCheckboxGroup({
    captions,
    order,
    sections,
    shouldSort,
    type,
  });

  return (
    <CheckboxGroup
      elementName={elementName}
      exclude={exclude}
      formControlStyle={formControlStyle}
      include={include}
      isRequired={isRequired}
      items={items}
      label={label != null ? label : getName(type, translations)}
      labelStyle={labelStyle}
      labelWrapperStyle={labelWrapperStyle}
      sideMutation={sideMutation}
      wrapperStyle={wrapperStyle}
    />
  );
}
